/*
  SwiftMoto custom: "Delivery and shipping" block on the product page.
  Markup and settings: snippets/main-product-delivery.liquid,
  Theme settings > Delivery estimate.

  Works in store time (the store's UTC offset, e.g. +0800), whatever the
  shopper's own time zone:
  - Dispatch: today if it's a dispatch day (Mon to Sat by default, never
    Sunday or a listed public holiday) and before the cutoff hour, else the
    next dispatch day. In a peak period, plus that period's extra dispatch days.
  - Delivery: dispatch day plus min to max courier working days (Mon to Fri by
    default, skipping public holidays). A peak period has its own min and max.
  - Free shipping: the chosen variant's price (less a running Sale price
    display sale) at or over the threshold, or a free shipping promotion
    that's on now and whose minimum the price meets.
  Re-checks every minute, so the cutoff passing while the page is open is
  picked up, and on every size or colour change (product:variant-change).
*/
(function () {
  'use strict';

  var DAY = 86400000;
  var NAMES = {
    en: {
      days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    },
    ms: {
      days: ['Ahd', 'Isn', 'Sel', 'Rab', 'Kha', 'Jum', 'Sab'],
      months: ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'],
    },
  };

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  // "+0800" -> 480 minutes
  function offsetMinutes(tz) {
    var m = /^([+-])(\d{2}):?(\d{2})$/.exec(String(tz || '').trim());
    if (!m) return 480;
    var mins = parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
    return m[1] === '-' ? -mins : mins;
  }

  // "2026-11-11" or "2026-11-11 23:59" (store time) -> Unix ms, or null.
  // A date without a time is the start of that day, or its end when endOfDay.
  function parseStoreTime(text, offset, endOfDay) {
    var m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/.exec(String(text || '').trim());
    if (!m) return null;
    var hasTime = m[4] !== undefined;
    var ms = Date.UTC(+m[1], +m[2] - 1, +m[3], hasTime ? +m[4] : 0, hasTime ? +m[5] : 0);
    if (!hasTime && endOfDay) ms += DAY - 60000;
    return ms - offset * 60000;
  }

  // Day number (days since 1970-01-01) of a "YYYY-MM-DD" at the start of a line
  function holidayDays(text) {
    var set = {};
    var last = null;
    String(text || '')
      .split(/\r?\n/)
      .forEach(function (line) {
        var m = /^\s*(\d{4})-(\d{2})-(\d{2})/.exec(line);
        if (!m) return;
        var d = Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3]) / DAY);
        set[d] = true;
        if (last === null || d > last) last = d;
      });
    return { set: set, last: last };
  }

  function dayOfWeek(day) {
    return (((day + 4) % 7) + 7) % 7; // 1970-01-01 was a Thursday
  }

  function format(day, lang) {
    var names = NAMES[lang] || NAMES.en;
    var d = new Date(day * DAY);
    return names.days[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + names.months[d.getUTCMonth()];
  }

  // Writes a template like "Estimated delivery [from] to [to]" into el,
  // with each value in bold. Text only, no HTML from settings.
  function fill(el, template, values) {
    el.textContent = '';
    var parts = String(template || '').split(/(\[[a-z]+\])/);
    parts.forEach(function (part) {
      var key = /^\[([a-z]+)\]$/.exec(part);
      if (key && Object.prototype.hasOwnProperty.call(values, key[1])) {
        var strong = document.createElement('strong');
        strong.textContent = values[key[1]];
        el.appendChild(strong);
      } else if (part) {
        el.appendChild(document.createTextNode(part));
      }
    });
  }

  function Delivery(root) {
    var configEl = root.querySelector('[data-product-delivery-config]');
    if (!configEl) return;
    try {
      this.c = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    var c = this.c;
    this.root = root;
    this.offset = offsetMinutes(c.tz);
    this.lang = c.lang === 'ms' ? 'ms' : 'en';
    this.variant = String(c.variant);
    this.holidays = holidayDays(c.holidays);
    this.peaks = (c.peaks || [])
      .map(function (p) {
        var start = parseStoreTime(p[0], this.offset, false);
        var end = parseStoreTime(p[1], this.offset, true);
        if (start === null || end === null || end < start) return null;
        return {
          start: start,
          end: end,
          extra: Math.max(0, toInt(p[2], 0)),
          min: Math.max(0, toInt(p[3], 1)),
          max: Math.max(0, toInt(p[4], 1)),
        };
      }, this)
      .filter(Boolean);
    var promo = c.promo || [];
    this.promo = {
      on: promo[0] === true,
      start: promo[1] ? parseStoreTime(promo[1], this.offset, false) : null,
      end: promo[2] ? parseStoreTime(promo[2], this.offset, true) : null,
      bad: (promo[1] && parseStoreTime(promo[1], this.offset, false) === null) ||
        (promo[2] && parseStoreTime(promo[2], this.offset, true) === null),
      min: toInt(promo[3], 0),
    };
    this.el = {
      dispatch: root.querySelector('[data-product-delivery-dispatch]'),
      estimate: root.querySelector('[data-product-delivery-estimate]'),
      free: root.querySelector('[data-product-delivery-free]'),
      threshold: root.querySelector('[data-product-delivery-threshold]'),
      note: root.querySelector('[data-product-delivery-note]'),
    };

    var self = this;
    document.addEventListener('product:variant-change', function (event) {
      var v = event.detail && event.detail.variant;
      // Ignore other products on the page (quick view, complementary products)
      if (v && v.id && self.c.variants[String(v.id)]) {
        self.variant = String(v.id);
        self.render();
      }
    });
    setInterval(function () {
      self.render();
    }, 60000);
    this.render();
  }

  Delivery.prototype.isHoliday = function (day) {
    return this.holidays.set[day] === true;
  };

  Delivery.prototype.isDispatchDay = function (day) {
    var dow = dayOfWeek(day);
    if (dow === 0 || this.isHoliday(day)) return false;
    if (dow === 6 && this.c.dispatchSat !== true) return false;
    return true;
  };

  Delivery.prototype.isCourierDay = function (day) {
    var dow = dayOfWeek(day);
    if (dow === 0 || this.isHoliday(day)) return false;
    if (dow === 6 && this.c.courierSat !== true) return false;
    return true;
  };

  Delivery.prototype.nextDay = function (day, test) {
    var d = day + 1;
    for (var i = 0; i < 60 && !test.call(this, d); i++) d++;
    return d;
  };

  // Returns {today, dispatch, from, to} as day numbers in store time
  Delivery.prototype.dates = function (nowMs) {
    var storeMs = nowMs + this.offset * 60000;
    var today = Math.floor(storeMs / DAY);
    var minuteOfDay = Math.floor((storeMs - today * DAY) / 60000);
    var cutoff = Math.min(24, Math.max(0, toInt(this.c.cutoff, 12)));

    var dispatch = today;
    if (!this.isDispatchDay(today) || minuteOfDay >= cutoff * 60) {
      dispatch = this.nextDay(today, this.isDispatchDay);
    }

    var min = Math.max(0, toInt(this.c.min, 1));
    var max = Math.max(0, toInt(this.c.max, 2));
    for (var p = 0; p < this.peaks.length; p++) {
      var peak = this.peaks[p];
      if (nowMs >= peak.start && nowMs <= peak.end) {
        for (var e = 0; e < peak.extra; e++) dispatch = this.nextDay(dispatch, this.isDispatchDay);
        min = peak.min;
        max = peak.max;
        break;
      }
    }
    if (max < min) max = min;

    var from = dispatch;
    for (var a = 0; a < min; a++) from = this.nextDay(from, this.isCourierDay);
    if (min === 0 && !this.isCourierDay(from)) from = this.nextDay(from, this.isCourierDay);
    var to = from;
    for (var b = min; b < max; b++) to = this.nextDay(to, this.isCourierDay);

    return { today: today, dispatch: dispatch, from: from, to: to };
  };

  Delivery.prototype.price = function (nowMs) {
    var v = this.c.variants[this.variant];
    if (!v) return null;
    var price = toInt(v[0], 0);
    var sale = this.c.sale || [0, 0, 0];
    var percent = toInt(sale[0], 0);
    var start = toInt(sale[1], 0) * 1000;
    var end = toInt(sale[2], 0) * 1000;
    if (percent > 0 && (!start || nowMs >= start) && (!end || nowMs < end)) {
      // Same rounding as snippets/price.liquid
      price -= Math.floor((price * percent + 50) / 100);
    }
    return price;
  };

  Delivery.prototype.isFree = function (nowMs) {
    var price = this.price(nowMs);
    if (price === null) return false;
    var p = this.promo;
    if (p.on && !p.bad && (p.start === null || nowMs >= p.start) && (p.end === null || nowMs <= p.end) && price >= p.min) {
      return true;
    }
    var threshold = toInt(this.c.threshold, 0);
    return threshold > 0 && price >= threshold;
  };

  Delivery.prototype.render = function () {
    var nowMs = Date.now();
    var v = this.c.variants[this.variant];
    var ships = !!(v && v[1] === true);

    if (ships) {
      var d = this.dates(nowMs);
      var dispatchLabel = format(d.dispatch, this.lang);
      if (d.dispatch === d.today && this.c.today) dispatchLabel = this.c.today;
      else if (d.dispatch === d.today + 1 && this.c.tomorrow) dispatchLabel = this.c.tomorrow;

      fill(this.el.dispatch.querySelector('span'), this.c.dispatchText, { date: dispatchLabel });

      var deliveryTemplate = this.c.deliveryText;
      if (d.from === d.to) deliveryTemplate = String(deliveryTemplate).replace(/\[from\][\s\S]*\[to\]/, '[from]');
      fill(this.el.estimate.querySelector('span'), deliveryTemplate, {
        from: format(d.from, this.lang),
        to: format(d.to, this.lang),
      });
    }
    this.el.dispatch.hidden = !ships;
    this.el.estimate.hidden = !ships;

    var free = this.isFree(nowMs);
    this.el.free.hidden = !free;
    this.el.threshold.hidden = free;

    // Theme editor only: warn when the holiday list is about to run out
    if (this.el.note) {
      var notes = [];
      var today = Math.floor((nowMs + this.offset * 60000) / DAY);
      if (this.holidays.last === null || this.holidays.last < today + 60) {
        notes.push('Delivery estimate: the public holiday list in Theme settings > Delivery estimate ends within 2 months. Add next year\'s dates.');
      }
      if (this.promo.on && this.promo.bad) {
        notes.push('Delivery estimate: a free shipping promotion date can\'t be read, so the promotion is off. Use 2026-12-12 23:59.');
      }
      this.el.note.textContent = notes.join(' ');
      this.el.note.hidden = notes.length === 0;
    }
  };

  function init() {
    document.querySelectorAll('[data-product-delivery]').forEach(function (root) {
      if (root.__productDelivery) return;
      root.__productDelivery = new Delivery(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Theme editor: blocks are re-rendered when settings change
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();
